import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { actions, slotDetailSelector } from "reducers/slotDetailModal";
import { actions as notificationActions } from "reducers/notification";
import { NotificationStateType } from "reducers/types/notification";
import { UserSlotRequestType } from "sagas/types/slotDetailModal";
import { AppDispatch, RootState } from "store";
import { EnrolledDropdown } from "./EnrolledDropdown";
import { newPickerSchema } from "./schemas";
import { FormApi } from "final-form";
import "../i18n";
import { useTranslation } from "react-i18next";
import { EnrolledDropdownContainerProps, NewPickerEnrolledType } from "./types";

const EnrolledDropdownContainer = (
  props: EnrolledDropdownContainerProps & ConnectorProps
): JSX.Element => {
  const { t } = useTranslation();
  const addNewPicker = () => {
    if (props.newPicker && props.slot) {
      props.addNewPicker({
        pickerId: props?.newPicker?.pickerId,
        slotId: props?.slot?.id,
      });
    }
  };

  const validatePicker = (userId: string, form: FormApi<object, object>) => {
    const id = Number(userId);
    if (!userId) {
      form.reset();
      form.resetFieldState("id" as never);
      props.resetNewPicker();
      props.setPickerDirty(false);
    } else if (userId && !isNaN(id))
      props.validatePicker({
        pickerId: id,
        slotId: props.slot.id,
      });
  };

  const showDeleteNotification = (onAccept: Function) => {
    props.showNotification({
      id: "notification-delete",
      level: "warning",
      title: t("slotDetail:deletePickerTitle"),
      body: t("slotDetail:deletePickerMessage"),
      onConfirmLabel: t("slotDetail:deletePickerButton"),
      onCancelLabel: "global:cancel",
      onConfirm: onAccept,
    });
  };

  const deletePicker = (userId: number) => {
    if (userId && props?.slot?.id) {
      showDeleteNotification(() =>
        props.deletePicker({
          pickerId: userId,
          slotId: props?.slot?.id || 0,
        })
      );
    }
  };

  const validationSchema = useMemo(() => {
    return newPickerSchema(props.pickerErrorCode);
  }, [props.pickerErrorCode]);

  const initialValues: NewPickerEnrolledType = {
    id: "",
    name: "-",
    phone: "-",
  };

  return (
    <EnrolledDropdown
      {...props}
      validationSchema={validationSchema}
      addNewPicker={addNewPicker}
      validatePicker={validatePicker}
      deletePicker={deletePicker}
      initialValues={initialValues}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  slot: slotDetailSelector(state).slot,
  users: slotDetailSelector(state).pickersEnrolled,
  newPicker: slotDetailSelector(state).newPicker,
  pickerErrorCode: slotDetailSelector(state).pickerErrorCode,
  newPickerDirty: slotDetailSelector(state).newPickerDirty,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validatePicker: (params: UserSlotRequestType) => {
    dispatch(actions.validatePicker(params));
  },
  deletePicker: (params: UserSlotRequestType) => {
    dispatch(actions.deletePickerRequest(params));
  },
  addNewPicker: (params: UserSlotRequestType) => {
    dispatch(actions.addNewPickerRequest(params));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  resetNewPicker: () => {
    dispatch(actions.resetNewPicker());
  },
  setPickerDirty: (dirty: boolean) => {
    dispatch(actions.setPickerDirty(dirty));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(EnrolledDropdownContainer);
